import { z } from 'zod';
import {
  AssortmentAddBySearchComponentProps,
  AssortmentAddViewComponentProps,
  ConfigurableGridComponentProps,
  AssortmentPublishComponentProps,
  CanvasViewComponentProps,
  CategorySummaryComponentProps,
  CollectionViewComponentProps,
  ColumnGroupedViewComponentProps,
  EnhancedOvertimeComponentProps,
  GridViewComponentProps,
  HistoryGridComponentProps,
  NestedAttributeComponentProps,
  NestedOvertimeComponentProps,
  ParameterTogglesComponentProps,
  zParetoAnalysisComponentProps,
  zParetoDetailsComponentProps,
  zEditableOverTimeGridComponentProps,
  ProductDetailsComponentProps,
  ProductivityComponentProps,
  ProductMixComponentProps,
  QuickTrendsComponentProps,
  RouteToLocationComponentProps,
  SizeEligibilityListGridComponentProps,
  StyleEditComponentProps,
  SummaryViewComponentProps,
  TargetListComponentProps,
  TopPerformersComponentProps,
  WorklistComponentProps,
  ExceptionsSummaryComponentProps,
  AssortmentCartViewComponentProps,
  zGeoTrendsComponentProps,
  zMfpSummaryGridComponentProps,
  zMacroMixComponentProps,
  zSummaryTopdownComponentProps,
  zMfpSplitViewComponentProps,
  zMfpSmartPlanComponentProps,
  zBulkImportComponentProps,
  zMfpReviewPlansComponentProps,
  SubheaderDefnProps,
} from './confdefnComponentProps';
import { o_BaseSectionView } from './confdefnView';
import { ProplessComponents } from './literals';
import _ from 'lodash';
// import { AppState } from './bindings.types'; TODO fix
import { isWorklistTabWithComponentProps } from './Worklist';

// #region Assortment Components
const ProplessComponent = z.object({
  ...o_BaseSectionView,
  component: ProplessComponents,
  // propless components are given an empty object to make the types easier
  // SPIKE: move this to o_BaseSectionView?
  componentProps: z.object({}).default({}),
});

const AssortmentCartViewComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('AssortmentCart'),
  componentProps: AssortmentCartViewComponentProps,
});

export interface AssortmentCartViewComponent extends z.infer<typeof AssortmentCartViewComponent> {}

const CollectionViewComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('CollectionView'),
  componentProps: CollectionViewComponentProps,
});

export interface CollectionViewComponent extends z.infer<typeof CollectionViewComponent> {}

const CanvasViewComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('CanvasView'),
  componentProps: CanvasViewComponentProps,
});

export interface CanvasViewComponent extends z.infer<typeof CanvasViewComponent> {}

const SummaryViewComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('SummaryView'),
  componentProps: SummaryViewComponentProps,
});

export interface SummaryViewComponent extends z.infer<typeof SummaryViewComponent> {}

const GridViewComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('GridView'),
  componentProps: GridViewComponentProps,
});

export interface GridViewComponent extends z.infer<typeof GridViewComponent> {}

const ColumnGroupedViewComponent = z.object({
  ...o_BaseSectionView,
  component: z.union([z.literal('FlowType'), z.literal('TopTYvsLY'), z.literal('FloorsetComparison')]),
  componentProps: ColumnGroupedViewComponentProps,
});

export interface ColumnGroupedViewComponent extends z.infer<typeof ColumnGroupedViewComponent> {}

const zQuickTrendsComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('QuickTrends'),
  componentProps: QuickTrendsComponentProps,
});

export interface QuickTrendsComponent extends z.infer<typeof zQuickTrendsComponent> {}

const CategorySummaryComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('CategorySummary'),
  componentProps: CategorySummaryComponentProps,
});

export interface CategorySummaryComponent extends z.infer<typeof CategorySummaryComponent> {}

const ProductMixComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('ProductMix'),
  componentProps: ProductMixComponentProps,
  overflow: z.string().optional().default('auto'),
});

export interface ProductMixComponent extends z.infer<typeof ProductMixComponent> {}

const ProductivityComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('Productivity'),
  componentProps: ProductivityComponentProps,
});

export interface ProductivityComponent extends z.infer<typeof ProductivityComponent> {}

const NestedAttributeComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('NestedAttribute'),
  componentProps: NestedAttributeComponentProps,
});

export interface NestedAttributeComponent extends z.infer<typeof NestedAttributeComponent> {}

const ProductDetailsComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('ProductDetails'),
  componentProps: ProductDetailsComponentProps,
});

export interface ProductDetailsComponent extends z.infer<typeof ProductDetailsComponent> {}

const WorklistComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('Worklist'),
  componentProps: WorklistComponentProps,
});

export interface WorklistComponent extends z.infer<typeof WorklistComponent> {}

const RouteToLocationComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('RouteToLocation'),
  componentProps: RouteToLocationComponentProps,
});

export interface RouteToLocationComponent extends z.infer<typeof RouteToLocationComponent> {}

const StyleEditComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('StyleEdit'),
  componentProps: StyleEditComponentProps,
});

export interface StyleEditComponent extends z.infer<typeof StyleEditComponent> {}

const zEditableOverTimeGridComponent = z.object({
  ...o_BaseSectionView,
  component: z.union([z.literal('PricingOverTime'), z.literal('FlowSheetByStyle')]),
  componentProps: zEditableOverTimeGridComponentProps,
});

export interface EditableOverTimeGridComponent extends z.infer<typeof zEditableOverTimeGridComponent> {}

const GridComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('ConfigurableGrid'),
  componentProps: ConfigurableGridComponentProps,
});

export interface GridComponent extends z.infer<typeof GridComponent> {}

const AssortmentAddViewComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('AssortmentAddView'),
  componentProps: AssortmentAddViewComponentProps,
});

export interface AssortmentAddViewComponent extends z.infer<typeof AssortmentAddViewComponent> {}

const AssortmentAddBySearchComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('AssortmentAddBySearch'),
  componentProps: AssortmentAddBySearchComponentProps,
});

export interface AssortmentAddBySearchComponent extends z.infer<typeof AssortmentAddBySearchComponent> {}

const EnhancedOvertimeComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('EnhancedOvertime'),
  componentProps: EnhancedOvertimeComponentProps,
});

export interface EnhancedOvertimeComponent extends z.infer<typeof EnhancedOvertimeComponent> {}

const NestedOvertimeCombineComponent = z.object({
  ...o_BaseSectionView,
  component: z.union([z.literal('NestedOvertime'), z.literal('NestedStyleOvertime')]),
  componentProps: NestedOvertimeComponentProps,
});

export interface NestedOvertimeCombineComponent extends z.infer<typeof NestedOvertimeCombineComponent> {}

const AssortmentPublishComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('AssortmentPublish'),
  componentProps: AssortmentPublishComponentProps,
});

export interface AssortmentPublishComponent extends z.infer<typeof AssortmentPublishComponent> {}

const TargetListComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('TargetList'),
  componentProps: TargetListComponentProps,
  // FIXME: this needs to be moved into componentProps
  allowNesting: z.boolean(),
});

export interface TargetListComponent extends z.infer<typeof TargetListComponent> {}

const SizeEligibilityListGridComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('SizeEligibilityListGrid'),
  componentProps: SizeEligibilityListGridComponentProps,
});

export interface SizeEligibilityListGridComponent extends z.infer<typeof SizeEligibilityListGridComponent> {}

const ParameterTogglesComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('ParameterToggles'),
  componentProps: ParameterTogglesComponentProps,
});

export interface ParameterTogglesComponent extends z.infer<typeof ParameterTogglesComponent> {}

const ParetoAnalysisComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('ParetoSummary'),
  componentProps: zParetoAnalysisComponentProps,
  overflow: z.string().optional().default('auto'),
});

const ParetoDetailsComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('ParetoDetails'),
  componentProps: zParetoDetailsComponentProps,
  overflow: z.string().optional().default('auto'),
});


export interface ParetoAnalysisComponent extends z.infer<typeof ParetoAnalysisComponent> {}
export interface ParetoDetailsComponent extends z.infer<typeof ParetoDetailsComponent> {}

const TopPerformersComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('TopPerformers'),
  componentProps: TopPerformersComponentProps,
});

export interface TopPerformersComponent extends z.infer<typeof TopPerformersComponent> {}

const ListAndNestedViewCombineComponent = z.object({
  ...o_BaseSectionView,
  component: z.union([z.literal('NestedView'), z.literal('ListView')]),
  componentProps: HistoryGridComponentProps,
});

export interface ListAndNestedViewCombineComponent extends z.infer<typeof ListAndNestedViewCombineComponent> {}

const zExceptionsSummaryComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('ExceptionsSummary'),
  componentProps: ExceptionsSummaryComponentProps,
});

export interface ExceptionsSummaryComponent extends z.infer<typeof zExceptionsSummaryComponent> {}

const zGeoTrendsComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('GeoTrends'),
  componentProps: zGeoTrendsComponentProps,
});

export interface GeoTrendsComponent extends z.infer<typeof zGeoTrendsComponent> {}

const zMacroMixComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('MacroMix'),
  componentProps: zMacroMixComponentProps,
});

export interface MacroMixComponent extends z.infer<typeof zMacroMixComponent> {}

const zBulkImportComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('BulkImport'),
  componentProps: zBulkImportComponentProps,
});

export interface BulkImportComponent extends z.infer<typeof zBulkImportComponent> {}

const zSummaryMacroTopDownComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('Summary'),
  componentProps: zSummaryTopdownComponentProps,
});

// #endregion

// #region MFP Components
const zMfpSummaryGridComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('MfpSummaryGrid'),
  componentProps: zMfpSummaryGridComponentProps,
});

export interface MfpSummaryGridComponent extends z.infer<typeof zMfpSummaryGridComponent> {}

const zMfpFavoriteComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('MfpFavorite'),
  componentProps: zMfpSummaryGridComponentProps,
});

export interface MfpFavoriteComponent extends z.infer<typeof zMfpFavoriteComponent> {}

const zMfpSplitViewComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('MfpSplitView'),
  componentProps: zMfpSplitViewComponentProps,
});

export interface MfpSplitViewComponent extends z.infer<typeof zMfpSplitViewComponent> {}

const zMfpSmartPlanComponent = z.object({
  ...o_BaseSectionView,
  component: z.literal('MfpSmartPlan'),
  componentProps: zMfpSmartPlanComponentProps,
});

export interface MfpSmartPlanComponent extends z.infer<typeof zMfpSmartPlanComponent> {}

const zMfpReviewPlansComponent = z.object({
  ...o_BaseSectionView,
  component: z.union([z.literal('MfpReviewPlans'), z.literal('MfpReviewPrivatePlans')]),
  componentProps: zMfpReviewPlansComponentProps,
});

export interface MfpReviewPlansComponent extends z.infer<typeof zMfpReviewPlansComponent> {}
// #endregion

// TODO: would like to intersect RouteComponentProps here instead of at each individual componets OwnProp declaration
// see this issue: https://github.com/colinhacks/zod/issues/53
// const RoutePropsSchema = toZod(RouteComponentProps);
// then .merge/.and(RoutPropsSchema)
export const SectionView = z.union([
  ProplessComponent,
  CollectionViewComponent,
  AssortmentCartViewComponent,
  CanvasViewComponent,
  SummaryViewComponent,
  GridViewComponent,
  ColumnGroupedViewComponent,
  CategorySummaryComponent,
  ProductMixComponent,
  ProductivityComponent,
  NestedAttributeComponent,
  ProductDetailsComponent,
  WorklistComponent,
  StyleEditComponent,
  zEditableOverTimeGridComponent,
  GridComponent,
  AssortmentAddViewComponent,
  AssortmentAddBySearchComponent,
  NestedOvertimeCombineComponent,
  ParetoAnalysisComponent,
  ParetoDetailsComponent,
  AssortmentPublishComponent,
  TargetListComponent,
  SizeEligibilityListGridComponent,
  ParameterTogglesComponent,
  TopPerformersComponent,
  ListAndNestedViewCombineComponent,
  EnhancedOvertimeComponent,
  zQuickTrendsComponent,
  RouteToLocationComponent,
  zExceptionsSummaryComponent,
  zGeoTrendsComponent,
  zMfpSummaryGridComponent,
  zMacroMixComponent,
  zSummaryMacroTopDownComponent,
  zMfpFavoriteComponent,
  zMfpSplitViewComponent,
  zMfpSmartPlanComponent,
  zMfpReviewPlansComponent,
  zBulkImportComponent,
]);
export type SectionViewConf = z.infer<typeof SectionView>;

/**
 * Used to determine the name value of the desired component if used in `ofComponentType`.
 * The string value needs to map directly to the `component` property value in the component validator
 */
export enum ConfDefnComponentType {
  collectionView = 'CollectionView',
  canvasView = 'CanvasView',
  summaryView = 'SummaryView',
  gridView = 'GridView',
  flowType = 'FlowType',
  topTyLy = 'TopTYvsLY',
  categorySummary = 'CategorySummary',
  productMix = 'ProductMix',
  productivity = 'Productivity',
  nestedAttribute = 'NestedAttribute',
  productDetails = 'ProductDetails',
  worklist = 'Worklist',
  floorsetComparison = 'FloorsetComparison',
  styleEdit = 'StyleEdit',
  pricing = 'PricingOverTime',
  flowSheet = 'FlowSheetByStyle',
  receiptGrid = 'ReceiptGrid',
  assortmentAddView = 'AssortmentAddView',
  assortmentAddBySearch = 'AssortmentAddBySearch',
  nestedOvertime = 'NestedOvertime',
  nestedStyleOverTime = 'NestedStyleOvertime',
  assortmentPublish = 'AssortmentPublish',
  targetList = 'TargetList',
  sizeEligibility = 'SizeEligibilityListGrid',
  parameterToggles = 'ParameterToggles',
  paretoSummary = 'ParetoSummary',
  paretoDetails = 'ParetoDetails',
  topPerformers = 'TopPerformers',
  nestedView = 'NestedView',
  listView = 'ListView',
  visualize = 'Visualize',
  enhancedOvertime = 'EnhancedOvertime',
  quickTrends = 'QuickTrends',
  configurableGrid = 'ConfigurableGrid',
  configurableGridView = 'ConfigurableGridView',
  routeToLocation = 'RouteToLocation',
  quickReconcile = 'QuickReconcile',
  geoTrends = 'GeoTrends',
  macroMix = 'MacroMix',
  summary = 'Summary',
  mfpFavorite = 'MfpFavorite',
  mfpSplitView = 'MfpSplitView',
  mfpSmartPlan = 'MfpSmartPlan',
  // propless components are listed below to be referenced via component type for error handling reporting purposes
  reporting = 'Reporting',
  bulkImport = 'BulkImport',
  assortmentCart = 'AssortmentCart',
  exceptionSummary = 'ExceptionsSummary',
  configEditor = 'ConfigEditor',
  mfpSummaryGrid = 'MfpSummaryGrid',
  mfpReviewPlans = 'MfpReviewPlans',
  mfpReviewPrivatePlans = 'MfpReviewPrivatePlans',
  mfpReporting = 'MfpReporting',
  mfpMassCopy = 'MfpMassCopy',
  mfpMassActualize = 'MfpMassActualize',
}

function isOfType<T extends Record<string, unknown>>(
  confType: ConfDefnComponentType,
  sectionView: T | undefined,
): sectionView is T {
  return !_.isNil(sectionView) && 'component' in sectionView && sectionView['component'] === confType;
}

/**
 * A partially dynamic helper method that removes the need for individual type predicates for each component above.
 * This is mainly used in epics to narrow down the types of the componentProps (a.k.a ownProps).
 *
 * e.g. isComponentType(ConfDefnComponentType.canvasView)
 *
 * @param {ConfDefnComponentType} componentType - the mapped name of the component type to verify.
 * @param {any} conf - the conf of the activePage or activeSubPage; the type is really a `SectionViewConf`
 *
 * @returns boolean
 */
export function isOfComponentType<T extends Record<string, unknown>>(
  type: ConfDefnComponentType,
  conf: any,
): conf is T {
  return isOfType<T>(type, conf);
}

export function maybeGetSubheaderComponentProps(state: any): SubheaderDefnProps | null {
  if (_.isNil(state.perspective)) {
    return null;
  }

  const activePageConf = state.perspective.activePageConf;
  const activeSubPage = state.perspective.activeSubPage;
  const worklistConf = state.worklist;

  // Check if activePageConf has subheader componentProps
  if (isOfComponentType<WorklistComponent>(ConfDefnComponentType.worklist, activePageConf)) {
    // Check if worklist has subheader componentProps
    if (activeSubPage && worklistConf && worklistConf.viewDefn) {
      const tabConf = worklistConf.viewDefn.tabs.find((tab: any) => {
        return tab.pathSlot === activeSubPage;
      });
      if (tabConf && tabConf.componentProps && tabConf.componentProps.defns && tabConf.componentProps.defns.subheader) {
        return tabConf.componentProps.defns.subheader;
      }
    }
  } else if (
    // this condition is second because the worklist could have a subheader defn
    activePageConf &&
    activePageConf.componentProps &&
    activePageConf.componentProps.defns &&
    activePageConf.componentProps.defns.subheader
  ) {
    return activePageConf.componentProps.defns.subheader;
  }

  return null;
}

export function maybeGetComponentProps<T extends SectionViewConf>(
  state: any,
  componentType: ConfDefnComponentType,
): T['componentProps'] | null {
  if (_.isNil(state.perspective)) {
    return null;
  }
  const activePageConf = state.perspective.activePageConf;
  const activeSubPage = state.perspective.activeSubPage;
  const worklistConf = state.worklist;

  if (isOfComponentType<T>(componentType, activePageConf)) {
    return activePageConf.componentProps;
  } else if (isOfComponentType<WorklistComponent>(ConfDefnComponentType.worklist, activePageConf)) {
    if (activeSubPage && worklistConf && worklistConf.viewDefn) {
      const tabConf = worklistConf.viewDefn.tabs.find((tab: any) => {
        return tab.pathSlot === activeSubPage;
      });
      if (tabConf && tabConf.componentType === componentType && isWorklistTabWithComponentProps(tabConf)) {
        return {
          //@ts-ignore
          ...tabConf.componentProps,
          topMembers: worklistConf.selectedItemId,
        };
      }
    }
  }

  return null;
}

export function isWorklistActive(state: any): boolean {
  const activePageConf = state.perspective.activePageConf;
  return isOfComponentType<WorklistComponent>(ConfDefnComponentType.worklist, activePageConf);
}

/**
 * This only detects correctly when navigating from worklist to non worklist of the same component type
 *
 * i.e Worklist -> Summary View Tab to Summary View
 */
export function isSameComponentType<T extends SectionViewConf>(
  state: any,
  componentType: ConfDefnComponentType,
): boolean {
  const activePageConf = state.perspective.activePageConf;
  const activeSubPage = state.perspective.activeSubPage;

  const isActivePage = isOfComponentType<T>(componentType, activePageConf);
  const isActiveSubPage = activeSubPage === componentType;

  return isActivePage && isActiveSubPage;
}
